import { useLingui } from '@lingui/react/macro';
import { ArrowSwapHorizontal, Clock, InfoCircle } from 'iconsax-reactjs';
import type { FC, ReactNode } from 'react';
import { Breadcrumb } from '@/base/Breadcrumb';
import { SEO_ORIGIN } from '@/base/constants';
import { PageMeta } from '@/base/PageMeta';
import { LandingLayout } from '@/landing/LandingLayout';
import { useSettings } from '@/settings/api';
import { usePairs } from '@/shared/api';
import { usePupeteerSignal } from '@/shared/usePupeteerSignal';
import { Widget } from '@/shared/Widget';
import { useFeeTiers, useMyTradeFees } from './api';
import { CurrentFeeClass } from './CurrentFeeClass';
import { FeeTable } from './FeeTable';

const SideCard: FC<{
  icon: ReactNode;
  title: string;
  description: ReactNode;
}> = ({ icon, title, description }) => (
  <Widget className="flex flex-col gap-2">
    <h2 className="text-base font-normal flex items-center gap-2">
      {icon}
      {title}
    </h2>
    <p className="text-sm text-content-tertiary leading-relaxed">
      {description}
    </p>
  </Widget>
);

export const FeePage: FC = () => {
  const { t } = useLingui();
  const [{ language }] = useSettings();
  const myFees = useMyTradeFees();
  const tiers = useFeeTiers();
  const pairs = usePairs();

  // The snapshot has to hold until all three have answered: the ladder, the
  // rates, and the pairs list the table takes its coin icons from.
  usePupeteerSignal(!!myFees.data && !!tiers.data && !!pairs.data);

  return (
    <LandingLayout>
      <PageMeta
        title={t`Trading Fees`}
        description={t`IQX trading fees — the maker and taker rate charged on every pair, with no hidden costs.`}
        localizedPath="/fee"
      />
      <div className="bg-background-disabled/40">
        <Breadcrumb
          className="w-full max-w-desktop mx-auto px-3"
          items={[
            { name: t`Home`, url: `${SEO_ORIGIN}/${language}` },
            { name: t`Trading Fees`, url: `${SEO_ORIGIN}/${language}/fee` },
          ]}
        />
      </div>
      <main className="w-full max-w-desktop mx-auto px-3 py-10 desktop:py-16 flex flex-col gap-widget">
        <header className="flex flex-col gap-3 max-w-2xl">
          <h1 className="text-2xl desktop:text-4xl font-semibold">
            {t`Trading fees`}
          </h1>
          <p className="text-base text-content-tertiary leading-relaxed">
            {t`Every spot trade on IQX is charged a single commission — nothing else. The rate depends on whether your order adds liquidity to the book or takes it away.`}
          </p>
        </header>

        <div className="grid grid-cols-1 desktop:grid-cols-3 gap-widget">
          <SideCard
            icon={<Clock size={18} className="text-content-tertiary" />}
            title={t`Maker`}
            description={t`Your order rests on the order book and is filled later by someone else. Makers add liquidity, so they pay the lower rate.`}
          />
          <SideCard
            icon={
              <ArrowSwapHorizontal
                size={18}
                className="text-content-tertiary"
              />
            }
            title={t`Taker`}
            description={t`Your order fills immediately against an order already on the book. Takers remove liquidity, so they pay the higher rate.`}
          />
          <SideCard
            icon={<InfoCircle size={18} className="text-content-tertiary" />}
            title={t`Which coin you pay in`}
            description={t`The fee is always charged in the coin you receive — the base coin on a buy, the quote coin on a sell — and is taken from the filled amount as the trade settles.`}
          />
        </div>

        <Widget className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="text-lg font-normal">{t`Fees by tier`}</h2>
            <p className="text-sm text-content-tertiary">
              {t`Every tier's rates, per pair. Tiers are ordered most expensive first.`}
            </p>
            <CurrentFeeClass className="mt-1" />
          </div>
          <FeeTable />
        </Widget>
      </main>
    </LandingLayout>
  );
};
